import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  Truck,
  Users,
  Building2,
  Globe,
  Lock,
} from "lucide-react";
import Swal from "sweetalert2";
import { DocumentTypeModal } from "./DocumentTypeModal";

const DocumentTypeManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entityFilter, setEntityFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedType, setSelectedType] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  useEffect(() => {
    if (user?.empresa_id) {
      loadDocumentTypes();
    }
  }, [user?.empresa_id]);

  useEffect(() => {
    filterDocumentTypes();
  }, [searchTerm, entityFilter, documentTypes]);

  const loadDocumentTypes = async () => {
    try {
      setLoading(true);
      const empresaId = user?.empresa_id;

      if (!empresaId) {
        Swal.fire(
          "Error",
          "No se pudo determinar la empresa del usuario",
          "error",
        );
        return;
      }

      // ✅ AGREGADO: Enviar empresa_id
      const response = await fetch(
        `${API_URL}/document-types?active_only=false&empresa_id=${empresaId}`,
        {
          headers: getHeaders(),
        },
      );

      if (response.ok) {
        const data = await response.json();
        console.log("📋 Tipos de documentos cargados:", data.length);
        console.log(
          "🌐 Tipos globales:",
          data.filter((t) => t.is_global).length,
        );
        console.log(
          "🏢 Tipos de mi empresa:",
          data.filter((t) => !t.is_global).length,
        );
        setDocumentTypes(data);
      } else {
        const error = await response.json();
        throw new Error(error.message || "Error al cargar tipos");
      }
    } catch (error) {
      console.error("Error cargando tipos de documentos:", error);
      Swal.fire(
        "Error",
        "No se pudieron cargar los tipos de documentos",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const filterDocumentTypes = () => {
    let filtered = [...documentTypes];

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (type) =>
          type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          type.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (type.description || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Filtro por entidad
    if (entityFilter !== "all") {
      filtered = filtered.filter((type) => type.entity_type === entityFilter);
    }

    setFilteredTypes(filtered);
  };

  const handleCreate = () => {
    setModalMode("create");
    setSelectedType(null);
    setShowModal(true);
  };

  const handleEdit = (type) => {
    // ✅ VALIDACIÓN: Solo editar si es editable
    if (!type.is_editable) {
      Swal.fire({
        icon: "warning",
        title: "No editable",
        text: type.is_global
          ? "Los tipos globales no se pueden editar. Puedes crear tu propia versión personalizada."
          : "Este tipo pertenece a otra empresa.",
      });
      return;
    }

    setModalMode("edit");
    setSelectedType(type);
    setShowModal(true);
  };

  const handleView = (type) => {
    setModalMode("view");
    setSelectedType(type);
    setShowModal(true);
  };

  const handleDelete = async (type) => {
    // ✅ VALIDACIÓN: Solo eliminar si es eliminable
    if (!type.is_deletable) {
      Swal.fire({
        icon: "warning",
        title: "No eliminable",
        text: type.is_global
          ? "Los tipos globales no se pueden eliminar."
          : "Este tipo pertenece a otra empresa.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "¿Eliminar tipo de documento?",
      html: `
        <p>¿Estás seguro de eliminar "<strong>${type.name}</strong>"?</p>
        ${type.custom_fields?.length > 0 ? `<p class="text-sm text-gray-600 mt-2">Este tipo tiene ${type.custom_fields.length} campo(s) personalizado(s).</p>` : ""}
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/document-types/${type.id}`, {
          method: "DELETE",
          headers: getHeaders(),
        });

        const data = await response.json();

        if (response.ok) {
          Swal.fire(
            "¡Eliminado!",
            "Tipo de documento eliminado correctamente",
            "success",
          );
          loadDocumentTypes();
        } else {
          throw new Error(data.message || "Error al eliminar");
        }
      } catch (error) {
        Swal.fire("Error", error.message, "error");
      }
    }
  };

  const handleToggleActive = async (type) => {
    // ✅ VALIDACIÓN: Solo activar/desactivar si es editable
    if (!type.is_editable) {
      Swal.fire({
        icon: "warning",
        title: "No modificable",
        text: "No puedes cambiar el estado de este tipo de documento.",
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/document-types/${type.id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ activo: !type.activo }),
      });

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: type.activo ? "Tipo desactivado" : "Tipo activado",
          timer: 1500,
          showConfirmButton: false,
        });
        loadDocumentTypes();
      } else {
        const error = await response.json();
        throw new Error(error.message || "Error al cambiar estado");
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case "vehicle":
        return <Truck className="h-5 w-5 text-blue-500" />;
      case "driver":
        return <Users className="h-5 w-5 text-purple-500" />;
      case "company":
        return <Building2 className="h-5 w-5 text-green-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getEntityLabel = (type) => {
    switch (type) {
      case "vehicle":
        return "Vehículo";
      case "driver":
        return "Conductor";
      case "company":
        return "Empresa";
      default:
        return "Desconocido";
    }
  };

  const getExpirationTypeLabel = (type) => {
    switch (type) {
      case "date":
        return "📅 Por Fecha";
      case "mileage":
        return "🛣️ Por Kilometraje";
      case "both":
        return "📅🛣️ Ambos";
      default:
        return "N/A";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-2.5 rounded-lg mr-3 shadow-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Tipos de Documentos
                </h1>
                <p className="text-sm text-gray-600">
                  Configuración de tipos de documentos y campos personalizados
                </p>
              </div>
            </div>

            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transition-all flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Tipo
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="px-4 sm:px-6 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por nombre, código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Filtro por Entidad */}
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            >
              <option value="all">Todas las Entidades</option>
              <option value="vehicle">🚗 Vehículos</option>
              <option value="driver">👤 Conductores</option>
              <option value="company">🏢 Empresa</option>
            </select>

            {/* Estadísticas */}
            <div className="flex items-center justify-end gap-4 text-sm">
              <div className="flex items-center">
                <Globe className="h-4 w-4 text-blue-500 mr-1" />
                <span className="font-medium">
                  {documentTypes.filter((t) => t.is_global).length} Globales
                </span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                <span className="font-medium">
                  {documentTypes.filter((t) => t.activo).length} Activos
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Cargando...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                      Código
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                      Nombre
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">
                      Entidad
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">
                      Vencimiento
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">
                      Campos
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTypes.length > 0 ? (
                    filteredTypes.map((type, index) => (
                      <tr
                        key={type.id}
                        className={`hover:bg-blue-50 transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-4">
                          <span className="font-mono text-sm font-medium text-gray-700">
                            {type.code}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <p className="font-semibold text-gray-900">
                              {type.name}
                            </p>
                            {type.description && (
                              <p className="text-xs text-gray-500">
                                {type.description.substring(0, 60)}
                                {type.description.length > 60 && "..."}
                              </p>
                            )}
                            {/* ✅ Badge si es global */}
                            {type.is_global && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 w-fit">
                                <Globe className="h-3 w-3 mr-1" />
                                Plantilla Global
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center">
                            {getEntityIcon(type.entity_type)}
                            <span className="ml-2 text-sm font-medium">
                              {getEntityLabel(type.entity_type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm">
                            {getExpirationTypeLabel(type.expiration_type)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {type.custom_fields?.length || 0} campos
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handleToggleActive(type)}
                            disabled={!type.is_editable}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              type.activo
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            } ${!type.is_editable ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            {type.activo ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Activo
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3 w-3 mr-1" />
                                Inactivo
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            {/* Ver - siempre disponible */}
                            <button
                              onClick={() => handleView(type)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                              title="Ver detalles"
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            {/* Editar - solo si es editable */}
                            {type.is_editable ? (
                              <button
                                onClick={() => handleEdit(type)}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                disabled
                                className="p-2 text-gray-300 cursor-not-allowed rounded-lg"
                                title={
                                  type.is_global
                                    ? "Tipo global - no editable"
                                    : "No tienes permisos"
                                }
                              >
                                <Lock className="h-4 w-4" />
                              </button>
                            )}

                            {/* Eliminar - solo si es eliminable */}
                            {type.is_deletable ? (
                              <button
                                onClick={() => handleDelete(type)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                disabled
                                className="p-2 text-gray-300 cursor-not-allowed rounded-lg"
                                title={
                                  type.is_global
                                    ? "Tipo global - no eliminable"
                                    : "No tienes permisos"
                                }
                              >
                                <Lock className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center">
                        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">
                          No se encontraron tipos de documentos
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <DocumentTypeModal
          mode={modalMode}
          documentType={selectedType}
          onClose={() => {
            setShowModal(false);
            setSelectedType(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setSelectedType(null);
            loadDocumentTypes();
          }}
        />
      )}
    </div>
  );
};

export default DocumentTypeManager;
